import { isFixedNodeId } from './config/fixedNodes';
import type { SharedFlowSnapshot } from './realtimeFlow';
import type { AppEdge, AppNode } from './types';

type IdFactory = () => string;
export type SharedFlowCopyMode = 'append' | 'replace';

const cloneNestedFlow = (
  nodes: AppNode[],
  edges: AppEdge[],
  createId: IdFactory,
): { nodes: AppNode[]; edges: AppEdge[] } => {
  const nodeIds = new Map<string, string>();
  nodes.forEach((node) => {
    nodeIds.set(node.id, isFixedNodeId(node.id) ? node.id : `node-${createId()}`);
  });

  const clonedNodes = nodes.map((node) => {
    const cloned = {
      ...node,
      id: nodeIds.get(node.id)!,
      selected: undefined,
      dragging: undefined,
      className: undefined,
    } as AppNode;
    if (cloned.type !== 'function') return cloned;
    const nested = cloneNestedFlow(cloned.data.internalNodes, cloned.data.internalEdges, createId);
    return {
      ...cloned,
      data: {
        ...cloned.data,
        internalNodes: nested.nodes,
        internalEdges: nested.edges,
      },
    };
  });

  const clonedEdges = edges.flatMap((edge) => {
    const source = nodeIds.get(edge.source);
    const target = nodeIds.get(edge.target);
    if (!source || !target) return [];
    return [{
      ...edge,
      id: `edge-${createId()}`,
      source,
      target,
      selected: undefined,
      className: undefined,
    }];
  });

  return { nodes: clonedNodes, edges: clonedEdges };
};

export const duplicateSharedFlow = (
  currentNodes: AppNode[],
  currentEdges: AppEdge[],
  snapshot: SharedFlowSnapshot,
  createId: IdFactory = () => crypto.randomUUID(),
): { nodes: AppNode[]; edges: AppEdge[]; addedNodeCount: number } => {
  const currentFixedIds = new Set(
    currentNodes.filter((node) => isFixedNodeId(node.id)).map((node) => node.id),
  );
  const sourceNodes = snapshot.nodes.filter((node) => !isFixedNodeId(node.id));
  if (sourceNodes.length === 0) {
    return { nodes: currentNodes, edges: currentEdges, addedNodeCount: 0 };
  }

  const sourceIds = new Map<string, string>();
  snapshot.nodes.forEach((node) => {
    if (isFixedNodeId(node.id)) {
      if (currentFixedIds.has(node.id)) sourceIds.set(node.id, node.id);
      return;
    }
    sourceIds.set(node.id, `node-${createId()}`);
  });

  const sourceMinimumX = Math.min(...sourceNodes.map((node) => node.position.x));
  const currentContentNodes = currentNodes.filter((node) => !isFixedNodeId(node.id));
  const currentMaximumX = currentContentNodes.length > 0
    ? Math.max(...currentContentNodes.map((node) => node.position.x))
    : 0;
  const offsetX = currentMaximumX - sourceMinimumX + 180;

  const duplicatedNodes = sourceNodes.map((node) => {
    const duplicated = {
      ...node,
      id: sourceIds.get(node.id)!,
      position: { x: node.position.x + offsetX, y: node.position.y + 40 },
      selected: undefined,
      dragging: undefined,
      className: undefined,
    } as AppNode;
    if (duplicated.type !== 'function') return duplicated;
    const nested = cloneNestedFlow(
      duplicated.data.internalNodes,
      duplicated.data.internalEdges,
      createId,
    );
    return {
      ...duplicated,
      data: {
        ...duplicated.data,
        internalNodes: nested.nodes,
        internalEdges: nested.edges,
      },
    };
  });

  const duplicatedEdges = snapshot.edges.flatMap((edge) => {
    const source = sourceIds.get(edge.source);
    const target = sourceIds.get(edge.target);
    if (!source || !target) return [];
    return [{
      ...edge,
      id: `edge-${createId()}`,
      source,
      target,
      selected: undefined,
      className: undefined,
    }];
  });

  return {
    nodes: currentNodes.concat(duplicatedNodes),
    edges: currentEdges.concat(duplicatedEdges),
    addedNodeCount: duplicatedNodes.length,
  };
};

export const replaceWithSharedFlow = (
  snapshot: SharedFlowSnapshot,
  createId: IdFactory = () => crypto.randomUUID(),
): { nodes: AppNode[]; edges: AppEdge[]; addedNodeCount: number } => {
  const duplicated = cloneNestedFlow(snapshot.nodes, snapshot.edges, createId);
  return {
    ...duplicated,
    addedNodeCount: duplicated.nodes.filter((node) => !isFixedNodeId(node.id)).length,
  };
};